'use strict';

import { updateUIFromShadowRoot } from '@core/i18n/window-language';
import { UPDATE_LANGUAGE_EVENT } from '@core/constants/EVENTS';
import { globalSharedStyles, tooltipsStyles, typographySharedStyles } from '@core/styles';
import {
    ACTIVE_ID_ATTRIBUTE,
    CUSTOM_FLEX_GROW_ATTRIBUTE,
    CURRENT_WESOG_PROVIDER_ATTRIBUTE,
    DETAILS_VIEW_ATTRIBUTE_IMG,
} from '@core/constants/ATTRIBUTES';
import { ripple } from '@core/utils/ripple';
import { goToSimilarImagesQuery } from '@core/utils/navigate';
import { PROV_FILTERS } from '@core/constants/API';
import { ZOOM_DOWN } from '@core/constants/CONSTANTS';
import { TOOLTIP_CONTENT_WIDTH_ATTRIBUTE } from '@components/TooltipComponent/CONSTANTS';

import { SEARCH_IMG_CARD_ATTRIBUTES } from '../../CONSTANTS';
import { SIMILAR_IMAGES_ICON_WEB_COMPONENT } from './CONSTANTS';
import { updateTooltipContent } from './utils';
import htmlContent from './SimilarImagesIconComponent.html';
import cssContent from './SimilarImagesIconComponent.css';

/**
 * SimilarImagesIconComponent
 *
 * A web component that provides a similar images icon with tooltip functionality.
 * Handles navigation to similar images and provides visual feedback.
 *
 * @class SimilarImagesIconComponent
 * @extends {HTMLElement}
 */
class SimilarImagesIconComponent extends HTMLElement {
    static get observedAttributes() {
        return [
            CUSTOM_FLEX_GROW_ATTRIBUTE.toLowerCase(),
            TOOLTIP_CONTENT_WIDTH_ATTRIBUTE.toLowerCase(),
            SEARCH_IMG_CARD_ATTRIBUTES.ZOOM_IN,
            CURRENT_WESOG_PROVIDER_ATTRIBUTE.toLowerCase(),
            DETAILS_VIEW_ATTRIBUTE_IMG.toLowerCase(),
            ACTIVE_ID_ATTRIBUTE.toLowerCase(),
        ];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        /** @type {HTMLTemplateElement} */
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(cssContent);

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            tooltipsStyles,
            typographySharedStyles,
            styleSheet,
        ];
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Initialize properties
        this._customFlexGrow = 0;
        this._tooltipContentWidth = 0;
        this._wesogProvider = null;
        this._activeId = null;
        this._img = null;
        this._zoomIn = false;

        this._tooltipSimilarImgsContentContainer = null;
        this._tooltipSimilarDropdownContent = null;
        this._tooltipSimilarDropdownContentText = null;
        this._tooltipSimilarDropdownContentImg = null;

        // Bind event handlers
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
        this._handleClickBound = this._handleClick.bind(this);
    }

    /**
     * Called when the element is connected to the DOM
     * @returns {void}
     * @override
     */
    connectedCallback() {
        this._cacheDOMElements();
        this._addEventListeners();
        this._addRippleBehavior();
        this.render();
    }

    /**
     * Called when the element is disconnected from the DOM
     * @returns {void}
     * @override
     */
    disconnectedCallback() {
        this._removeEventListeners();
    }

    /**
     * Called when observed attributes change
     * @param {string} name - The attribute name
     * @param {string} oldValue - The old attribute value
     * @param {string} newValue - The new attribute value
     * @returns {void}
     * @override
     */
    attributeChangedCallback(name, oldValue, newValue) {
        switch (name) {
            case CUSTOM_FLEX_GROW_ATTRIBUTE.toLowerCase():
                this._customFlexGrow = Number(newValue) || 0;
                break;
            case TOOLTIP_CONTENT_WIDTH_ATTRIBUTE.toLowerCase():
                this._tooltipContentWidth = Number(newValue) || 0;
                break;
            case SEARCH_IMG_CARD_ATTRIBUTES.ZOOM_IN.toLowerCase():
                this._zoomIn = newValue === 'true';
                break;
            case CURRENT_WESOG_PROVIDER_ATTRIBUTE.toLowerCase():
                this._wesogProvider = newValue;
                break;
            case ACTIVE_ID_ATTRIBUTE.toLowerCase():
                this._activeId = newValue;
                break;
            case DETAILS_VIEW_ATTRIBUTE_IMG.toLowerCase():
                this._img = JSON.parse(newValue);
                break;
        }

        this.render(false);
    }

    /**
     * Cache DOM element references for better performance
     * @returns {void}
     * @private
     */
    _cacheDOMElements() {
        this._imgSimilarImagesContainer = this.shadowRoot.querySelector(
            '[data-similar-images-icon]',
        );

        this._tooltipSimilarImgsContentContainer = this.shadowRoot.querySelector(
            '[data-similar-tooltip-dropdown]',
        );

        this._tooltipSimilarDropdownContentText = this.shadowRoot.querySelector(
            '#tooltip-similar-content > p',
        );

        this._tooltipSimilarDropdownContent = this.shadowRoot.getElementById(
            'tooltip-similar-content',
        );

        this._tooltipSimilarDropdownContentImg = this.shadowRoot.querySelector(
            '[data-similar-tooltip-placeholder',
        );
    }

    /**
     * Render the component
     * @param {boolean} firstTime - Whether this is the first render
     * @returns {void}
     */
    render(firstTime = true) {
        if (
            !this._tooltipSimilarDropdownContent ||
            !this._tooltipSimilarDropdownContentText ||
            !this._tooltipSimilarImgsContentContainer ||
            !this._imgSimilarImagesContainer ||
            !this._img
        ) {
            return;
        }

        /** @type {number} */
        let imgSimilarImagesContainerSize;
        if (this._zoomIn) {
            imgSimilarImagesContainerSize = 35 * ZOOM_DOWN;
            this._imgSimilarImagesContainer.style.width = `${imgSimilarImagesContainerSize}px`;
            this._imgSimilarImagesContainer.style.height = `${imgSimilarImagesContainerSize}px`;
            this._tooltipSimilarDropdownContentImg.style.width = `${imgSimilarImagesContainerSize}px`;
            this._tooltipSimilarDropdownContentImg.style.height = `${imgSimilarImagesContainerSize}px`;
        } else {
            imgSimilarImagesContainerSize = 40;
            this._imgSimilarImagesContainer.style.width = `${imgSimilarImagesContainerSize}px`;
            this._imgSimilarImagesContainer.style.height = `${imgSimilarImagesContainerSize}px`;
            this._tooltipSimilarDropdownContentImg.style.width = `${imgSimilarImagesContainerSize}px`;
            this._tooltipSimilarDropdownContentImg.style.height = `${imgSimilarImagesContainerSize}px`;
        }

        this._tooltipSimilarImgsContentContainer.style.left = `${
            this._img.img_width === 'auto' ? 40 : Math.min(this._customFlexGrow * 0.5, 40)
        }%`;

        this._tooltipSimilarDropdownContent.style.maxWidth = `${this._tooltipContentWidth}px`;

        updateTooltipContent(
            this._tooltipSimilarDropdownContentText,
            this._tooltipContentWidth,
        );

        if (!firstTime) return;

        // Add click handler only on first render
        this._imgSimilarImagesContainer.addEventListener('click', this._handleClickBound);
    }

    /**
     * Handle click events on the tooltip icon
     * @param {Event} e - The click event
     * @returns {Promise<void>}
     * @private
     */
    async _handleClick(e) {
        e.preventDefault();
        e.stopPropagation();

        goToSimilarImagesQuery(
            this._activeId,
            this._wesogProvider || PROV_FILTERS.GTRES,
            this._img.img_src,
        );
    }

    /**
     * Translate the component elements
     * @returns {void}
     * @private
     */
    _handleTranslationHelper() {
        updateUIFromShadowRoot(this.shadowRoot);
    }

    /**
     * Add event listeners to the component elements
     * @returns {void}
     * @private
     */
    _addEventListeners() {
        document.addEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );
    }

    /**
     * Remove event listeners to prevent memory leaks
     * @returns {void}
     * @private
     */
    _removeEventListeners() {
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );

        if (this._imgSimilarImagesContainer) {
            this._imgSimilarImagesContainer.removeEventListener(
                'click',
                this._handleClickBound,
            );
        }
    }

    /**
     * Add the ripple effect to elements with data-ripple attribute
     * @returns {void}
     * @private
     */
    _addRippleBehavior() {
        const rippleElements = this.shadowRoot.querySelectorAll('[data-ripple]');
        rippleElements.forEach((element) => {
            ripple(element);
        });
    }
}

customElements.define(SIMILAR_IMAGES_ICON_WEB_COMPONENT, SimilarImagesIconComponent);
